import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Link } from "react-router-dom";
import type { OrgDashboardTenant, OrgRecentRun } from "../../../api/client.ts";
import { formatNumber } from "../../../utils/format.ts";
import EmptyState from "../../EmptyState.tsx";
import Panel from "../../Panel.tsx";
import RelativeTime from "../../RelativeTime.tsx";
import { TenantAvatar } from "../../TenantAvatar.tsx";
import AgentRunStatusIcon from "../../agent/AgentRunStatusIcon.tsx";
import { agentTypeIcon, agentTypeLabel, runLink } from "./agentActivity.tsx";

interface RecentWorkPanelProps {
  runs: OrgRecentRun[];
  tenants: Map<string, OrgDashboardTenant>;
  showTenant: boolean;
}

/** The agents' latest finished runs: what each worked on, what it found and what it said. */
export default function RecentWorkPanel({ runs, tenants, showTenant }: RecentWorkPanelProps) {
  return (
    <Panel fill disablePadding title="Recent agent work" subtitle="The latest runs, newest first">
      {runs.length === 0 ? (
        <EmptyState title="No agent has run yet" description="Runs show up here as soon as a signal or an order sets an agent to work." />
      ) : (
        <Stack>
          {runs.map((run) => {
            const tenant = tenants.get(run.tenant_id);
            const to = runLink(run, tenant?.slug);
            const failed = run.status === "failed";
            return (
              <ButtonBase
                key={run.id}
                component={to ? Link : "div"}
                to={to}
                disabled={!to}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "flex-start",
                  textAlign: "left",
                  gap: 1.5,
                  px: 2,
                  py: 1.25,
                  borderBottom: 1,
                  borderColor: "divider",
                  "&:last-of-type": { borderBottom: 0 },
                  "&:hover": to ? { bgcolor: "action.hover" } : undefined,
                }}
              >
                <Box sx={{ color: failed ? "error.main" : "text.secondary", pt: 0.25, "& svg": { fontSize: 18 } }}>
                  {agentTypeIcon(run.agent_type)}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" spacing={0.75} sx={{ alignItems: "center", minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, flexShrink: 0 }}>
                      {run.agent_name ?? agentTypeLabel(run.agent_type)}
                    </Typography>
                    {run.subject && (
                      <Typography variant="body2" noWrap sx={{ color: "text.secondary", minWidth: 0 }}>
                        · {run.subject}
                      </Typography>
                    )}
                  </Stack>
                  {run.summary && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        mt: 0.25,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {run.summary}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "flex", alignItems: "center", gap: 0.75, mt: 0.25 }}>
                    {showTenant && tenant && (
                      <>
                        <TenantAvatar name={tenant.name} logoUrl={tenant.logo_url} size={14} />
                        <span>{tenant.name} ·</span>
                      </>
                    )}
                    <RelativeTime value={run.completed_at ?? run.created_at} />
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} sx={{ flexShrink: 0, alignItems: "center", pt: 0.25 }}>
                  {run.status === "completed" && run.companies_found > 0 && (
                    <Chip
                      size="small"
                      color="success"
                      variant="outlined"
                      label={`+${formatNumber(run.companies_found)} ${run.companies_found === 1 ? "company" : "companies"}`}
                    />
                  )}
                  <AgentRunStatusIcon status={run.status} agentName={run.agent_name ?? agentTypeLabel(run.agent_type)} />
                </Stack>
              </ButtonBase>
            );
          })}
        </Stack>
      )}
    </Panel>
  );
}
